import { useEffect, useState } from "react";
import type { Anchor, Rect } from "./types";

export const selectorFor = (target: Anchor): string =>
  `[data-onboarding-target="${target}"]`;

/**
 * Absent is not in the DOM, unsized, `display:none`, `visibility:hidden`,
 * or under an `inert` / `aria-hidden` ancestor the product put there (the
 * collapsed rail, a hidden section). Occluded is on screen but under a
 * Radix modal, menu or select: the `aria-hidden` package stamps
 * `data-aria-hidden` beside the `aria-hidden` it adds, and that marker is
 * the difference between a thing that is gone and a thing that is covered.
 */
export type TargetPresence = "absent" | "occluded" | "present";

export function presenceOf(el: Element | null): TargetPresence {
  if (!el) return "absent";
  let occluded = false;
  for (let n: Element | null = el; n; n = n.parentElement) {
    if (n.hasAttribute("inert")) return "absent";
    if (n.getAttribute("aria-hidden") === "true") {
      if (!n.hasAttribute("data-aria-hidden")) return "absent";
      occluded = true;
    }
  }
  const cs = getComputedStyle(el);
  if (cs.visibility === "hidden" || cs.display === "none") return "absent";
  const r = el.getBoundingClientRect();
  if (r.width < 1 || r.height < 1) return "absent";
  return occluded ? "occluded" : "present";
}

/** On screen, covered or not. */
export function isVisible(el: Element | null): el is HTMLElement {
  return presenceOf(el) !== "absent";
}

export interface Located {
  el: HTMLElement;
  presence: "occluded" | "present";
}

/**
 * The first match that is not absent. Some names render more than once (a
 * composer on every surface, the rail pane in both the desktop and drawer
 * branches), so the first match in document order is not the one on screen.
 */
export function locate(target: Anchor): Located | null {
  for (const el of document.querySelectorAll<HTMLElement>(
    selectorFor(target),
  )) {
    const presence = presenceOf(el);
    if (presence !== "absent") return { el, presence };
  }
  return null;
}

export const firstVisible = (target: Anchor): HTMLElement | null =>
  locate(target)?.el ?? null;

export function measure(el: HTMLElement): Rect {
  const r = el.getBoundingClientRect();
  const radius = parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0;
  return { top: r.top, left: r.left, width: r.width, height: r.height, radius };
}

const sameRect = (a: Rect | null, b: Rect): boolean =>
  !!a &&
  Math.abs(a.top - b.top) < 0.5 &&
  Math.abs(a.left - b.left) < 0.5 &&
  Math.abs(a.width - b.width) < 0.5 &&
  Math.abs(a.height - b.height) < 0.5 &&
  a.radius === b.radius;

export const TICK_MS = 100;

/**
 * Measure on a short interval, on resize, on any scroll and through a
 * ResizeObserver on the element; the shell's own transitions are followed
 * without a seam of their own. `measure` runs once before the first tick.
 */
export function observe(measure: () => void): () => void {
  measure();
  const tick = window.setInterval(measure, TICK_MS);
  window.addEventListener("resize", measure);
  window.addEventListener("scroll", measure, true);
  return () => {
    window.clearInterval(tick);
    window.removeEventListener("resize", measure);
    window.removeEventListener("scroll", measure, true);
  };
}

export interface TargetState {
  /** The live rect; null only while the target is absent. */
  rect: Rect | null;
  presence: TargetPresence;
}

const ABSENT: TargetState = { rect: null, presence: "absent" };

interface Tracked extends TargetState {
  /** Whose state this is: a new target reads absent until it is measured. */
  target: Anchor | null;
}

const NONE: Tracked = { ...ABSENT, target: null };

/**
 * The target's live rect, kept while it is merely occluded so the ring can
 * glide back without a jump; the presence rides beside it. The state names
 * its target, so the frame between a target change and its first measure
 * never shows the previous target's rect.
 */
export function useTargetRect(target: Anchor | null): TargetState {
  const [state, setState] = useState<Tracked>(NONE);
  useEffect(() => {
    if (!target) {
      setState(NONE);
      return undefined;
    }
    let el: HTMLElement | null = null;
    let last: TargetState = ABSENT;
    let ro: ResizeObserver | null =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => read())
        : null;
    const read = () => {
      const found = locate(target);
      const next = found?.el ?? null;
      if (next !== el) {
        if (ro) {
          ro.disconnect();
          if (next) ro.observe(next);
        }
        el = next;
      }
      if (!found) {
        if (last.rect) {
          last = ABSENT;
          setState({ ...ABSENT, target });
        }
        return;
      }
      const nr = measure(found.el);
      if (!sameRect(last.rect, nr) || last.presence !== found.presence) {
        last = { rect: nr, presence: found.presence };
        setState({ ...last, target });
      }
    };
    const stop = observe(read);
    return () => {
      stop();
      ro?.disconnect();
      ro = null;
    };
  }, [target]);
  return state.target === target ? state : ABSENT;
}

/**
 * The rect of every element a selector matches, for regions with no anchor
 * of their own (what the agent has placed on the canvas), followed on the
 * same cadence as a target. Null while nothing matches.
 */
export function useSelectorRects(
  selector: string | null,
): readonly Rect[] | null {
  const [rects, setRects] = useState<readonly Rect[] | null>(null);
  useEffect(() => {
    if (!selector) {
      setRects(null);
      return undefined;
    }
    let last: readonly Rect[] | null = null;
    return observe(() => {
      const next: Rect[] = Array.from(document.querySelectorAll(selector))
        .map((el) => el.getBoundingClientRect())
        .filter((r) => r.width >= 1 && r.height >= 1)
        .map((r) => ({
          top: r.top,
          left: r.left,
          width: r.width,
          height: r.height,
          radius: 0,
        }));
      if (!next.length) {
        if (last) {
          last = null;
          setRects(null);
        }
        return;
      }
      if (
        last &&
        last.length === next.length &&
        last.every((r, k) => sameRect(r, next[k]))
      )
        return;
      last = next;
      setRects(next);
    });
  }, [selector]);
  return rects;
}
